// Team (brief section 8.6; brief update 2 sections 7-16 and 23): the workspace's assistant roles,
// their responsibilities, main prompt, model and effort, every earlier version, and the members.
// Leads change and restore roles; members read them.
import { useState } from 'react';
import { useContextWorkspace } from '../components/Layout';
import { Chip, ErrorBox, Field, Loading, Modal, PageHead } from '../components/ui';
import { api, errorText } from '../lib/api';
import { when, words } from '../lib/format';
import { useSession } from '../lib/session';
import type { Agent, AgentVersion, Member } from '../lib/types';
import { useApi } from '../lib/useApi';

export function Team() {
  const ws = useContextWorkspace()!;
  const agents = useApi<Agent[]>(`/api/w/${ws.slug}/agents`);
  const members = useApi<Member[]>(`/api/w/${ws.slug}/members`);
  const shared = useApi<{ text: string }>('/api/team/shared-instructions');
  const [editing, setEditing] = useState<Agent | null>(null);
  const lead = ws.role === 'lead';

  return (
    <>
      <PageHead title="Team" sub={`Who does what in ${ws.name}: people and assistant roles.`} />
      <section className="panel">
        <h2>Assistant roles</h2>
        <p className="muted small">
          The {ws.kind === 'joint' ? 'joint chief of staff' : 'chief of staff'} and the experts answer in the Assistant now: choose who when
          you start a conversation. Each reads only what that conversation's readers may see. Advisers are consulted on selected
          questions from milestone 2.
        </p>
        {shared.data && (
          <details className="fold">
            <summary>Shared instructions every role starts with</summary>
            <p className="small pre">{shared.data.text}</p>
          </details>
        )}
        {agents.error && <ErrorBox error={agents.error} retry={agents.reload} />}
        {!agents.data && !agents.error && <Loading />}
        <ul className="roles">
          {agents.data?.map((a) => (
            <li key={a.id} className={`role${a.enabled ? '' : ' off'}`}>
              <div className="row between wrap gap">
                <div>
                  <h3>{a.name}</h3>
                  <div className="row gap wrap small">
                    <span className="badge">{words(a.kind)}</span>
                    {a.answers_now ? <Chip value="active" /> : <span className="muted">from milestone 2</span>}
                    {!a.enabled && <Chip value="paused" />}
                    {a.version !== null && <span className="muted">version {a.version}</span>}
                  </div>
                </div>
                <div className="small muted">
                  model: <strong>{a.model ?? 'workspace default'}</strong> · effort: <strong>{a.effort ?? 'default'}</strong>
                </div>
              </div>
              <p>{a.responsibilities}</p>
              {a.instructions && (
                <details className="fold">
                  <summary>Main prompt</summary>
                  <p className="small pre">{a.instructions}</p>
                </details>
              )}
              {lead && (
                <button type="button" className="btn small" onClick={() => setEditing(a)}>
                  Edit
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
      <section className="panel">
        <h2>People</h2>
        {members.error && <ErrorBox error={members.error} retry={members.reload} />}
        <ul className="recs">
          {members.data?.map((m) => (
            <li key={m.id} className="rec">
              <div className="rec-main">
                <strong>{m.name}</strong> <span className="muted small">{m.email}</span> <span className="badge">{m.role}</span>
                {!m.signed_in && <span className="muted small"> · invited, not signed in yet</span>}
              </div>
            </li>
          ))}
        </ul>
        <p className="muted small">Members are managed in Settings.</p>
      </section>
      {editing && (
        <AgentForm
          agent={editing}
          path={`/api/w/${ws.slug}/agents/${editing.id}`}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void agents.reload();
          }}
        />
      )}
    </>
  );
}

export function AgentForm({
  agent,
  path,
  onClose,
  onSaved,
  personal,
}: {
  agent: Agent;
  path: string;
  onClose: () => void;
  onSaved: (a: Agent) => void;
  personal?: boolean;
}) {
  const { me } = useSession();
  const [form, setForm] = useState({
    name: agent.name,
    responsibilities: agent.responsibilities,
    instructions: agent.instructions,
    enabled: agent.enabled,
    model: agent.model ?? '',
    effort: agent.effort ?? '',
    note: '',
  });
  const [error, setError] = useState('');
  const versions = useApi<AgentVersion[]>(`${path}/versions`);

  async function restore(version: number) {
    setError('');
    try {
      onSaved(await api<Agent>(`${path}/restore`, { body: { version } }));
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      onSaved(
        await api<Agent>(path, {
          method: 'PATCH',
          body: {
            name: form.name,
            responsibilities: form.responsibilities,
            instructions: form.instructions,
            enabled: personal ? undefined : form.enabled,
            model: form.model || undefined,
            effort: form.effort || undefined,
            clear_model: !form.model,
            clear_effort: !form.effort,
            note: form.note.trim() || undefined,
          },
        }),
      );
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <Modal title={`Edit ${agent.name}`} onClose={onClose} wide>
      <form onSubmit={save}>
        <Field label="Name">
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={120} />
        </Field>
        <Field label="Responsibilities">
          <textarea rows={3} value={form.responsibilities} onChange={(e) => setForm({ ...form, responsibilities: e.target.value })} />
        </Field>
        <Field label="Main prompt" hint="What this role is told after the shared instructions. Every saved change is kept below.">
          <textarea rows={10} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
        </Field>
        <div className="row gap wrap">
          <Field label="Model" hint="Empty = the workspace default.">
            <select value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })}>
              <option value="">default</option>
              {me.models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label} (${m.input_per_mtok}/${m.output_per_mtok} per M tokens)
                </option>
              ))}
            </select>
          </Field>
          <Field label="Effort" hint="More effort: deeper answers, slower and dearer.">
            <select value={form.effort} onChange={(e) => setForm({ ...form, effort: e.target.value })}>
              <option value="">default</option>
              {['low', 'medium', 'high', 'xhigh', 'max'].map((x) => (
                <option key={x} value={x}>
                  {x}
                </option>
              ))}
            </select>
          </Field>
          {!personal && (
            <label className="check">
              <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} /> Enabled
            </label>
          )}
        </div>
        <Field label="Why (optional)" hint="Kept with this version, for example the feedback that led to it.">
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} maxLength={300} />
        </Field>
        {error && <p className="msg err">{error}</p>}
        <footer className="modal-foot">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary">
            Save
          </button>
        </footer>
      </form>
      <section aria-label="Earlier versions">
        <h3>Versions</h3>
        {versions.error && <ErrorBox error={versions.error} retry={versions.reload} />}
        <ul className="versions">
          {versions.data?.map((v, i) => (
            <li key={v.version}>
              <div className="row between wrap gap">
                <span className="small">
                  <strong>Version {v.version}</strong> · {when(v.at)} · {v.by}
                  {i === 0 ? ' · current' : ''}
                </span>
                {i > 0 && (
                  <button type="button" className="btn small" onClick={() => void restore(v.version)}>
                    Restore version {v.version}
                  </button>
                )}
              </div>
              {v.note && <p className="small muted">{v.note}</p>}
              <details className="fold">
                <summary>
                  {v.name}
                  {v.enabled ? '' : ' (off)'} · model {v.model ?? 'default'} · effort {v.effort ?? 'default'}
                </summary>
                <p className="small">{v.responsibilities}</p>
                <p className="small pre">{v.instructions || '(no main prompt)'}</p>
              </details>
            </li>
          ))}
        </ul>
      </section>
    </Modal>
  );
}
